const isProductionHost = ["hairbridge.me", "www.hairbridge.me"].includes(
  window.location.hostname,
);

export const API_URL =
  import.meta.env.VITE_API_URL ||
  (isProductionHost
    ? "https://hairbridge-api.wjstks3474.workers.dev"
    : "https://hairbridge-api-preview.wjstks3474.workers.dev");

export const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ||
  (isProductionHost ? "0x4AAAAAAD3uez3BaV0IltNa" : undefined);

let lastRequestId:string|null=null;
export const latestRequestId=()=>lastRequestId;
export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    public requestId?:string,
  ) {
    super(code);
    if(requestId)lastRequestId=requestId;
  }
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  requestId?:string;
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
  token?: string,
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    signal: options.signal || AbortSignal.timeout(10_000),
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const body = (await response
    .json()
    .catch(() => null)) as ApiResponse<T> | null;
  if (!response.ok || !body?.success)
    throw new ApiError(body?.error || "network_error", response.status, response.headers.get("x-request-id") || body?.requestId || undefined);
  return body.data;
}

export async function apiForm<T>(
  path: string,
  body: FormData,
  token: string,
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    method: "POST",
    signal: AbortSignal.timeout(15_000),
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  const payload = (await response
    .json()
    .catch(() => null)) as ApiResponse<T> | null;
  if (!response.ok || !payload?.success)
    throw new ApiError(payload?.error || "network_error", response.status, response.headers.get("x-request-id") || payload?.requestId || undefined);
  return payload.data;
}

export function errorMessage(error: unknown): string {
  const code = error instanceof ApiError ? error.code : "network_error";
  const messages: Record<string, string> = {
    recovery_read_only:"안전 점검 중에는 저장한 기록만 볼 수 있어요. 변경은 복구 뒤 다시 시도해 주세요.",
    outside_booking_window: "예약 마감 시간이 지났거나 아직 열리지 않은 날짜예요. 다른 시간을 골라 주세요.",
    recurring_break: "디자이너가 쉬는 시간이에요. 다른 시간을 골라 주세요.",
    cancel_window_closed: "온라인 취소 마감이 지났어요. 디자이너에게 연락해 주세요.",
    online_booking_unavailable: "지금은 온라인 예약을 받을 수 없어요. 디자이너에게 직접 문의해 주세요.",
    break_outside_business_hours: "쉬는 시간을 영업 시간 안으로 골라 주세요.",
    photo_consent_required: "손님의 사진 게시 동의를 먼저 확인해 주세요.",
    invalid_recovery_code:"복구 코드가 맞지 않아요. 코드를 다시 확인해 주세요.",
    recovery_locked:"복구 코드를 5번 확인하지 못했어요. 1시간 뒤 다시 시도해 주세요.",
    review_edit_window_closed:"후기 수정은 작성 후 30일 안에 할 수 있어요. 삭제는 언제든 가능해요.",
    archive_asset_missing:"내려받을 사진을 찾지 못했어요. 사진 없이 데이터를 받거나 고객지원에 문의해 주세요.",
    pass_balance_insufficient:"회원권 잔액이 부족해요. 사용 금액과 남은 잔액을 확인해 주세요.",
    no_rebooking_slot:"추천 주기에 빈 시간이 없어요. 달력에서 다른 날짜를 골라 주세요.",
    change_window_closed:"온라인 변경 마감이 지났어요. 디자이너에게 연락해 주세요.",
    inquiry_not_found:"질문을 찾을 수 없어요. 저장한 질문 링크를 확인해 주세요.",
    invalid_input: "입력 내용을 다시 확인해 주세요.",
    invalid_credentials: "이메일 또는 비밀번호가 맞지 않습니다.",
    email_already_registered: "이미 가입된 이메일입니다.",
    slug_unavailable: "이미 사용 중인 예약 주소입니다.",
    slot_unavailable:
      "방금 다른 고객이 선택한 시간입니다. 다른 시간을 골라 주세요.",
    idempotency_key_conflict: "같은 예약 확인 키로 다른 내용을 보낼 수 없습니다. 입력을 확인해 주세요.",
    booking_transition_conflict: "예약 상태가 방금 바뀌었습니다. 새로고침해 최신 상태를 확인해 주세요.",
    rate_limit_exceeded: "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
    designer_not_found: "공개된 디자이너 페이지를 찾을 수 없습니다.",
    designer_or_service_not_found: "현재 예약할 수 없는 서비스입니다.",
    turnstile_token_required: "사람인지 확인한 뒤 다시 시도해 주세요.",
    turnstile_rejected: "보안 확인에 실패했습니다. 다시 시도해 주세요.",
    turnstile_unavailable:
      "보안 확인 서비스에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.",
    turnstile_not_configured:
      "보안 확인 설정을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
    booking_cannot_be_cancelled: "이미 완료되거나 취소된 예약입니다.",
    booking_already_started: "예약 시간이 지나 온라인으로 취소할 수 없습니다.",
    booking_lifecycle_not_available:
      "확정된 미래 예약에서만 시간 확인 또는 조정 요청을 남길 수 있습니다.",
    consultation_not_available:
      "진행 중인 예약에서만 시술 전 확인표를 수정할 수 있습니다.",
    consultation_source_not_available:
      "같은 디자이너의 완료된 예약에서 가져올 수 있는 원하는 결과를 찾지 못했습니다.",
    rebooking_not_available: "지금은 이 재방문 후보를 바꿀 수 없습니다.",
    review_not_available: "완료된 예약에만 후기를 남길 수 있습니다.",
    review_already_submitted: "이미 후기를 남긴 예약입니다.",
    authentication_required: "로그인이 필요합니다.",
    invalid_or_expired_token: "로그인이 만료되었습니다. 다시 로그인해 주세요.",
    oauth_not_configured: "소셜 로그인 설정을 준비하고 있습니다.",
    oauth_state_invalid: "로그인 요청이 만료되었습니다. 다시 시작해 주세요.",
    oauth_provider_cancelled: "소셜 로그인이 취소되었습니다.",
    oauth_provider_error:
      "소셜 로그인 제공자 연결에 실패했습니다. 다시 시도해 주세요.",
    oauth_email_required: "이메일 제공에 동의해야 가입할 수 있습니다.",
    oauth_account_conflict:
      "같은 이메일 계정이 있습니다. 기존 방식으로 로그인한 뒤 계정을 연결해 주세요.",
    oauth_exchange_invalid:
      "로그인 확인 코드가 만료되었습니다. 다시 로그인해 주세요.",
    oauth_provider_invalid: "지원하지 않는 소셜 로그인입니다.",
    booking_not_found: "예약 정보를 찾을 수 없습니다.",
    booking_action_expired: "이미 답변했거나 만료된 예약 알림입니다.",
    client_not_found: "고객 기록을 찾을 수 없습니다.",
    client_already_exists: "같은 연락처의 고객이 이미 원장에 있습니다.",
    client_media_not_found: "비공개 시술 사진을 찾을 수 없습니다.",
    inventory_item_exists: "같은 이름의 재고 품목이 이미 있습니다.",
    inventory_item_not_found: "재고 품목을 찾을 수 없습니다.",
    inventory_below_zero: "보유 수량보다 많이 사용할 수 없습니다.",
    calendar_range_invalid: "조회할 일정 범위를 다시 확인해 주세요.",
    calendar_block_invalid: "예약 불가 시간은 30분 단위로 입력해 주세요.",
    calendar_block_not_found: "예약 불가 시간을 찾을 수 없습니다.",
    calendar_feed_not_found: "폐기됐거나 유효하지 않은 캘린더 주소입니다.",
    scheduled_time_invalid: "예약 시간은 30분 단위로 입력해 주세요.",
    scheduled_time_out_of_range: "예약 시간은 지금부터 1년 안으로 입력해 주세요.",
    outside_business_hours: "설정한 예약 가능 시간 안에서 선택해 주세요.",
    service_not_found: "활성화된 서비스를 선택해 주세요.",
    image_required: "포트폴리오 이미지를 선택해 주세요.",
    image_type_not_allowed: "JPG, PNG, WebP 이미지만 올릴 수 있습니다.",
    image_size_invalid: "이미지는 5MB 이하로 올려 주세요.",
    image_signature_invalid: "이미지 파일 형식을 확인해 주세요.",
    portfolio_not_found: "포트폴리오를 찾을 수 없습니다.",
    designer_account_required:
      "이 계정은 이용객 계정입니다. 내 예약에서 이어서 보거나 디자이너 계정으로 로그인해 주세요.",
    listing_not_ready:
      "공개 전에 활동명, 지역, 전문 시술, 서비스와 예약 가능 시간을 모두 채워 주세요.",
    discovery_date_out_of_range:
      "오늘부터 90일 안의 날짜를 선택해 주세요.",
    review_not_found: "후기를 찾을 수 없습니다.",
    review_report_exists: "이미 검토를 요청한 후기입니다.",
    review_report_not_found: "후기 검토 요청을 찾을 수 없습니다.",
    // 이 문구는 회원가입·로그인·고객 등록·CSV 가져오기 등 모든 화면에서 함께
    // 쓰인다. 예약 전용 문장을 넣으면 가입 실패 화면에 "예약은 확정되지
    // 않았습니다"가 뜬다. 예약 맥락의 안내는 예약 화면에서 따로 덧붙인다.
    network_error:
      "연결이 끊겨 요청이 완료되지 않았습니다. 인터넷 연결을 확인한 뒤 다시 시도해 주세요.",
    active_booking_requires_cancellation_first:
      "진행 중인 예약은 먼저 취소해야 연락처와 요청 내용을 지울 수 있습니다.",
    booking_privacy_delete_conflict:
      "삭제 상태를 확인하지 못했습니다. 화면을 새로고침한 뒤 다시 확인해 주세요.",
    booking_export_failed:
      "예약 정보를 내보내지 못했습니다. 관리 링크를 확인한 뒤 다시 시도해 주세요.",
    account_export_failed:
      "계정 정보를 내보내지 못했습니다. 다시 로그인한 뒤 시도해 주세요.",
    reauthentication_failed:
      "비밀번호가 맞지 않아 계정 삭제 요청을 시작하지 않았습니다.",
  };
  return messages[code] || "처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

export function track(
  name: string,
  properties: Record<string, unknown> = {},
): void {
  let anonymousId:string=crypto.randomUUID();
  try {anonymousId=localStorage.getItem("hb_anonymous_id")||anonymousId;localStorage.setItem("hb_anonymous_id",anonymousId);}catch{/* No durable identity is claimed when storage is unavailable. */}
  void api("/api/v1/analytics/events", {
    method: "POST",
    body: JSON.stringify({ name, anonymousId, properties }),
  }).catch(() => undefined);
}
